'use client';

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, ChevronDown, Check } from 'lucide-react';
import { FilterProps, SortOption, DateFilter } from '@/types/filters';
import { useDebounce } from '@/hooks/useDebounce';
import { config } from '@/config/config';

import { format } from "date-fns";
import { cn } from "@/lib/utils";
import useEventCountries from '@/hooks/cachedCalls/use-event-countries';
import useEventProvinces from '@/hooks/cachedCalls/use-event-provinces';
import useSports from '@/hooks/cachedCalls/use-sports';
import { DayPickerSingleProps } from 'react-day-picker';
import { Dictionary } from '@/dictionaries';
import Spinner from '../ui/spinner';
import { useCachedSports } from '@/hooks/useCachedSports';
import { useCountrySelectionModal } from '../providers/CountrySelectionModalProvider';
import { useClientCountry } from '../i18n/ClientCountryProvider';
import { useAuth } from '@/hooks/use-auth';
import Link from 'next/link';
import { createSportSlug, createCategorySlug } from '@/utils/slugs';
import { useRouter } from 'next/navigation';

interface EventFiltersProps extends Omit<FilterProps, 'lang'> {
  dictionary: Dictionary;
  lang: string;
  contextFilters?: {
    sport?: string;
    sportName?: string;
    subsport?: string;
  };
  hideCountryFilter?: boolean;
  hideProvinceFilter?: boolean;
  hidePreferencesLink?: boolean;
  enableSportNavigation?: boolean; // Nueva prop para habilitar navegación por deporte
}

const EventFilters: React.FC<EventFiltersProps> = ({
  filters,
  onFiltersChange,
  lang = 'es',
  dictionary,
  contextFilters,
  hideCountryFilter = false,
  hideProvinceFilter = false,
  hidePreferencesLink = false,
  enableSportNavigation = false
}) => {
  const router = useRouter();
  
  // Estado local para los filtros temporales (antes de aplicar)
  const [localFilters, setLocalFilters] = useState(filters);

  const [showCustomDates, setShowCustomDates] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>(filters.dateFilter?.startDate);
  const [endDate, setEndDate] = useState<Date | undefined>(filters.dateFilter?.endDate);
  const [searchValue, setSearchValue] = useState(filters.pattern || '');

  const { openModal } = useCountrySelectionModal();
  const { currentCountryData } = useClientCountry();
  const { isAuthenticated } = useAuth();

  const [displayedProvincesCount, setDisplayedProvincesCount] = useState(10);
  const [provinceSearchValue, setProvinceSearchValue] = useState('');
  const [isProvinceDropdownOpen, setIsProvinceDropdownOpen] = useState(false);
  const debouncedProvinceSearch = useDebounce(provinceSearchValue, 300);

  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [dateSearchValue, setDateSearchValue] = useState('');
  const debouncedDateSearch = useDebounce(dateSearchValue, 300);

  const [isOrderDropdownOpen, setIsOrderDropdownOpen] = useState(false);

  const [isSportDropdownOpen, setIsSportDropdownOpen] = useState(false);
  const [sportSearchValue, setSportSearchValue] = useState('');
  const debouncedSportSearch = useDebounce(sportSearchValue, 300);

  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isStartDatePopoverOpen, setIsStartDatePopoverOpen] = useState(false);
  const [isEndDatePopoverOpen, setIsEndDatePopoverOpen] = useState(false);
  const [categorySearchValue, setCategorySearchValue] = useState('');
  const debouncedCategorySearch = useDebounce(categorySearchValue, 300);

  const [selectedSport, setSelectedSport] = useState<string | undefined>(filters.sport || contextFilters?.sport);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedProvinces, setSelectedProvinces] = useState<number[]>([]);

  // Sincronizar localFilters cuando cambian los filters del padre
  useEffect(() => {
    setLocalFilters(filters);
    setSearchValue(filters.pattern || '');
    setSelectedSport(filters.sport || contextFilters?.sport);
    setSelectedCategories([...filters.categories || []]);
    setSelectedProvinces([...filters.provinces || []]);
  }, [filters, contextFilters?.sport]);

  const combinedFilters = useMemo(() => ({
    ...localFilters,
    ...(contextFilters?.sport && { sport: contextFilters.sport }),
    ...(contextFilters?.subsport && { subsport: contextFilters.subsport })
  }), [localFilters, contextFilters?.sport, contextFilters?.subsport]);

  const { data: countries } = useEventCountries();
  const { data: provinces, loading: loadingProvinces } = useEventProvinces(lang, combinedFilters);
  
  // Obtener deportes con traducciones
  const { data: sportsData, loading: loadingSports } = useSports([lang, 'en']);
  const [sports, englishSports] = sportsData;
  const cachedSports = useCachedSports();

  const sportId: string | null = selectedSport || contextFilters?.sport || null;

  // Lista de deportes disponibles para el dropdown (solo los que tienen eventos)
  const availableSports = useMemo(() => {
    if (!sports || sports.length === 0) return [];
    
    // Si aún no se han cargado los deportes con eventos, mostrar todos (para evitar mostrar vacío mientras carga)
    if (cachedSports === null) {
      return sports.map(sport => ({
        id: sport.idDeporte.toString(),
        name: sport.nombre
      }));
    }
    
    // Filtrar solo deportes que tienen eventos
    return sports
      .filter(sport => cachedSports.some(cachedSport => cachedSport.idDeporte === sport.idDeporte))
      .map(sport => ({
        id: sport.idDeporte.toString(),
        name: sport.nombre
      }));
  }, [sports, cachedSports]);

  const categories = useMemo(() => {
    if (!sportId) return [];

    return Object.entries(dictionary.sportsTranslations.subsports[sportId] || {})
      .map(([key, value]) => ({
        key: key,
        label: value
      }))
      .filter(cat => cat.label && typeof cat.label === 'string');
  }, [sportId, dictionary.sportsTranslations.subsports])
  
  // Filtrar deportes por búsqueda
  const filteredSports = useMemo(() => {
    if (!availableSports || availableSports.length === 0) return [];
    
    return availableSports.filter(sport =>
      sport.name && sport.name.toLowerCase().includes(debouncedSportSearch.toLowerCase())
    );
  }, [availableSports, debouncedSportSearch]);

  // Filtrar categorías por búsqueda
  const filteredCategories = useMemo(() => {
    if (!categories || categories.length === 0) return [];
    
    if (!debouncedCategorySearch) return categories;
    
    return categories.filter(cat =>
      cat.label && typeof cat.label === 'string' && 
      cat.label.toLowerCase().includes(debouncedCategorySearch.toLowerCase())
    );
  }, [categories, debouncedCategorySearch]);

  const getSelectedSportText = () => {
    if (!selectedSport) return t.allSports || 'Todos los deportes';
    if (!availableSports || availableSports.length === 0) return t.allSports || 'Todos los deportes';
    const sport = availableSports.find(s => s.id === selectedSport);
    return sport?.name || t.allSports || 'Todos los deportes';
  };

  const getSelectedCountriesText = () => {
    return currentCountryData?.name || t.allCountries;
  };

  const getSelectedProvincesText = () => {
    if (selectedProvinces.length === 0) return t.allProvinces;
    return selectedProvinces.map(provinceId => provinces.find(p => p.id === provinceId)?.value).join(', ');
  };

  const filteredAndOrderedProvinces = provinces
    .filter(province =>
      province.value.toLowerCase().includes(debouncedProvinceSearch.toLowerCase())
    )
    .sort((a, b) => a.value.localeCompare(b.value, lang));

  const t = (dictionary?.filters || {}) as any;

  const formatDateByLanguage = (date: Date) => {
    const pattern = lang === 'en' ? 'MM/dd/yyyy' : 'dd/MM/yyyy';
    return format(date, pattern);
  };

  const dateFilterOptions = [
    { key: 'past-events' as const, label: t.pastEvents },
    { key: 'future-events' as const, label: t.futureEvents },
    { key: 'this-weekend' as const, label: t.thisWeekend },
    { key: 'next-week' as const, label: t.nextWeek },
    { key: 'next-month' as const, label: t.nextMonth },
    { key: 'custom' as const, label: t.custom }
  ];

  const orderOptions: { key: string, label: string, field: 'name' | 'date', order: 'asc' | 'desc' }[] = [
    { key: 'name-desc' as const, label: t.nameDescending, field: 'name' as const, order: 'desc' as const },
    { key: 'name-asc' as const, label: t.nameAscending, field: 'name' as const, order: 'asc' as const },
    { key: 'date-desc' as const, label: t.dateDescending, field: 'date' as const, order: 'desc' as const }
  ];

  if (localFilters.dateFilter?.type !== 'past-events') {
    orderOptions.push({ key: 'date-asc' as const, label: t.dateAscending, field: 'date' as const, order: 'asc' as const });
  }

  const selectedDateFilters = localFilters.dateFilter ? [localFilters.dateFilter.type] : [];

  const getSelectedDateFiltersText = () => {
    if (selectedDateFilters.length === 0 || selectedDateFilters[0] === 'all') return t.allDateFilters;
    if (selectedDateFilters.length === 1) {
      const option = dateFilterOptions.find(opt => opt.key === selectedDateFilters[0]);
      return option?.label || selectedDateFilters[0];
    }
    return `${selectedDateFilters.length} ${t.selectedFilters}`;
  };

  const getSelectedOrderText = () => {
    if (!localFilters.sort) return t.dateAscending;

    const option = orderOptions.find(opt =>
      opt.field === localFilters.sort?.field && opt.order === localFilters.sort?.order
    );
    return option?.label || t.dateAscending;
  };

  const filteredDateOptions = dateFilterOptions.filter(option =>
    option.label.toLowerCase().includes(debouncedDateSearch.toLowerCase())
  );

  const getSelectedCategoryText = () => {
    if (selectedCategories.length == 0) return t.allCategories;
    return selectedCategories
      .map(cat => categories.find(category => category.key === cat)?.label)
      .filter(Boolean)
      .join(', ') || t.allCategories;
  };

  // Sincronizar filtro de país con el país seleccionado globalmente
  useEffect(() => {
    if (currentCountryData?.id) {
      setLocalFilters(prev => {
        if (parseInt(currentCountryData.id) !== prev.country) {
          return {
            ...prev,
            country: parseInt(currentCountryData.id),
            provinces: [] // Resetear provincias cuando cambia el país
          };
        }
        return prev;
      });
    }
  }, [currentCountryData?.id]);

  // Validar que el país seleccionado existe en la lista de países disponibles
  useEffect(() => {
    if (countries.length > 0) {
      setLocalFilters(prev => {
        if (prev.country) {
          const countryExists = countries.some(country => country.id === prev.country);
          if (!countryExists) {
            return {
              ...prev,
              country: undefined,
              provinces: [] // También resetear provincias si el país no existe
            };
          }
        }
        return prev;
      });
    }
  }, [countries]);

  // Validar que las provincias seleccionadas existen en la lista de provincias disponibles
  useEffect(() => {
    if (!loadingProvinces && provinces.length > 0) {
      setLocalFilters(prev => {
        if (prev.provinces && prev.provinces.length > 0) {
          const validProvinces = prev.provinces.filter(provinceId =>
            provinces.some(province => province.id === provinceId)
          );
          if (validProvinces.length !== prev.provinces.length) {
            return {
              ...prev,
              provinces: validProvinces
            };
          }
        }
        return prev;
      });
    }
  }, [provinces, loadingProvinces]);

  useEffect(() => {
    setDisplayedProvincesCount(10);
  }, [debouncedProvinceSearch]);

  useEffect(() => {
    if (localFilters.dateFilter?.type === 'custom') {
      setShowCustomDates(true);
      setStartDate(localFilters.dateFilter.startDate);
      setEndDate(localFilters.dateFilter.endDate);
    } else {
      setShowCustomDates(false);
      setStartDate(undefined);
      setEndDate(undefined);
    }
  }, [localFilters.dateFilter]);

  const handleSortChange = (field: 'name' | 'date', order: 'asc' | 'desc') => {
    const newSort: SortOption = { field, order };
    setLocalFilters({ ...localFilters, sort: newSort });
    setIsOrderDropdownOpen(false);
  };


  const handleAllDateFiltersSelect = () => {
    setShowCustomDates(false);
    setStartDate(undefined);
    setEndDate(undefined);
    setLocalFilters({ ...localFilters, dateFilter: { type: 'all' } });
    setIsDateDropdownOpen(false);
  };

  const handleDateFilterSelect = (type: DateFilter['type']) => {
    const currentFilters = localFilters.dateFilter ? [localFilters.dateFilter.type] : [];

    if (currentFilters.includes(type)) {
      const newFilters = currentFilters.filter(filter => filter !== type);

      if (type === 'custom') {
        setShowCustomDates(false);
        setStartDate(undefined);
        setEndDate(undefined);
      }

      if (newFilters.length === 0) {
        setLocalFilters({ ...localFilters, dateFilter: undefined });
      } else {
        const activeFilter = newFilters[0];
        if (activeFilter === 'custom') {
          setLocalFilters({
            ...localFilters,
            dateFilter: { type: activeFilter, startDate, endDate }
          });
        } else {
          setLocalFilters({ ...localFilters, dateFilter: { type: activeFilter } });
        }
      }
    } else {
      if (type === 'custom') {
        setShowCustomDates(true);
        if (startDate || endDate) {
          setLocalFilters({
            ...localFilters,
            dateFilter: { type, startDate, endDate }
          });
        } else {
          setLocalFilters({
            ...localFilters,
            dateFilter: { type, startDate: undefined, endDate: undefined }
          });
        }
      } else {
        setShowCustomDates(false);
        setStartDate(undefined);
        setEndDate(undefined);

        if (type === 'past-events' && localFilters.sort?.field === 'date' && localFilters.sort?.order === 'asc') {
          setLocalFilters({
            ...localFilters,
            dateFilter: { type },
            sort: { field: 'date', order: 'desc' }
          });
        } else {
          setLocalFilters({ ...localFilters, dateFilter: { type } });
        }
      }
    }

    setIsDateDropdownOpen(false);
  };

  const handleStartDateChange = (date: Date | undefined) => {
    // Normalizar la fecha a medianoche en la zona horaria local
    const normalizedDate = date ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0) : undefined;

    setStartDate(normalizedDate);
    if (normalizedDate || endDate) {
      const newFilter = {
        type: 'custom' as const,
        startDate: normalizedDate,
        endDate: endDate
      };
      setLocalFilters({
        ...localFilters,
        dateFilter: newFilter
      });
    } else {
      setLocalFilters({ ...localFilters, dateFilter: undefined });
    }
    // Cerrar el popover después de seleccionar la fecha
    setIsStartDatePopoverOpen(false);
  };

  const handleEndDateChange = (date: Date | undefined) => {
    // Normalizar la fecha a medianoche en la zona horaria local
    const normalizedDate = date ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0) : undefined;

    setEndDate(normalizedDate);
    if (startDate || normalizedDate) {
      const newFilter = {
        type: 'custom' as const,
        startDate: startDate,
        endDate: normalizedDate
      };
      setLocalFilters({
        ...localFilters,
        dateFilter: newFilter
      });
    } else {
      setLocalFilters({ ...localFilters, dateFilter: undefined });
    }
    // Cerrar el popover después de seleccionar la fecha
    setIsEndDatePopoverOpen(false);
  };


  const handleProvinceChange = (provinceId: number) => {
    const currentProvinces = [...selectedProvinces];

    if (currentProvinces.includes(provinceId)) {
      currentProvinces.splice(currentProvinces.indexOf(provinceId), 1);
    } else {
      currentProvinces.push(provinceId);
    }

    setSelectedProvinces(currentProvinces);
  };

  const handleAllProvincesSelect = () => {
    setSelectedProvinces([]);
  };

  const handleShowMoreProvinces = () => {
    setDisplayedProvincesCount(prev => prev * 2);
  };

  const handleShowLessProvinces = () => {
    setDisplayedProvincesCount(prev => Math.max(10, Math.floor(prev / 2)));
  };

  const handleCategoryChange = (categoryKey: string) => {
    const currentFilters = [...(selectedCategories || [])];

    if (currentFilters.includes(categoryKey)) {
      currentFilters.splice(currentFilters.indexOf(categoryKey), 1);
    } else {
      currentFilters.push(categoryKey);
    }

    setSelectedCategories(currentFilters);
  };

  const handleAllCategoriesSelect = () => {
    setSelectedCategories([]);
  };

  const handleSportChange = (sportId: string | undefined) => {
    setSelectedSport(sportId);
    // Si cambia el deporte, resetear categorías
    if (sportId !== selectedSport) {
      setSelectedCategories([]);
    }
  };

  const handleAllSportsSelect = () => {
    setSelectedSport(undefined);
    setSelectedCategories([]);
  };

  // Función para navegar según el deporte y categorías seleccionadas
  const navigateToSportRoute = async () => {
    if (!enableSportNavigation || !englishSports || englishSports.length === 0) return;

    // Si no hay deporte seleccionado, ir a allEvents
    if (!selectedSport) {
      router.push(`/${lang}/allEvents`);
      return;
    }

    // Obtener el nombre del deporte en inglés para construir el slug
    const sportEnglish = englishSports.find(s => s.idDeporte.toString() === selectedSport)?.nombre;
    if (!sportEnglish) return;

    const sportSlug = createSportSlug(sportEnglish);

    // Si no hay categorías seleccionadas, ir a la página del deporte
    if (!selectedCategories || selectedCategories.length === 0) {
      router.push(`/${lang}/sport/${sportSlug}`);
      return;
    }

    // Si hay categorías, obtener sus nombres en INGLÉS para construir la ruta
    // Importar el diccionario inglés dinámicamente
    const englishTranslations = await import(`@/locales/en/common.json`);
    
    const categoriesEnglish = selectedCategories.map(catId => {
      const catName = englishTranslations.default.sportsTranslations.subsports[selectedSport]?.[catId];
      return catName;
    }).filter(Boolean);

    if (categoriesEnglish.length > 0) {
      const categoryPath = categoriesEnglish.map(cat => createCategorySlug(cat as string)).join('/');
      router.push(`/${lang}/sport/${sportSlug}/categories/${categoryPath}`);
    } else {
      router.push(`/${lang}/sport/${sportSlug}`);
    }
  };

  const applyFilters = () => {
    // Si está habilitada la navegación por deporte y cambió el deporte o categorías
    if (enableSportNavigation && (selectedSport !== filters.sport || 
        JSON.stringify(selectedCategories) !== JSON.stringify(filters.categories))) {
      navigateToSportRoute();
      return;
    }

    const filtersToApply = {
      ...localFilters,
      pattern: searchValue,
      sport: selectedSport,
      categories: selectedCategories,
      provinces: selectedProvinces
    };

    onFiltersChange(filtersToApply);
  };

  const clearFilters = () => {
    setShowCustomDates(false);
    setStartDate(undefined);
    setEndDate(undefined);
    setSearchValue('');
    setProvinceSearchValue('');
    setDisplayedProvincesCount(10);
    setIsProvinceDropdownOpen(false);
    setDateSearchValue('');
    setIsDateDropdownOpen(false);
    setIsOrderDropdownOpen(false);
    setSportSearchValue('');
    setIsSportDropdownOpen(false);
    setCategorySearchValue('');
    setIsCategoryDropdownOpen(false);
    setIsStartDatePopoverOpen(false);
    setIsEndDatePopoverOpen(false);
    setSelectedSport(undefined);
    setSelectedCategories([]);
    setSelectedProvinces([]);

    // Si está habilitada la navegación, ir a allEvents
    if (enableSportNavigation) {
      router.push(`/${lang}/allEvents`);
      return;
    }

    const clearedFilters = {
      sort: { field: 'date' as const, order: 'asc' as const },
      dateFilter: { type: 'future-events' as const },
      country: currentCountryData ? parseInt(currentCountryData.id) : undefined
    };

    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const isDateFilterActive = (type: DateFilter['type']) => {
    if (type === 'custom') {
      return localFilters.dateFilter?.type === type;
    }
    return localFilters.dateFilter?.type === type;
  };

  const isCategoryDropdownOpenRef = useRef(isCategoryDropdownOpen);
  const isProvinceDropdownOpenRef = useRef(isProvinceDropdownOpen);

  useEffect(() => {
    if (isCategoryDropdownOpenRef.current && !isCategoryDropdownOpen) {
      setLocalFilters(prev => {
        if (JSON.stringify(selectedCategories) !== JSON.stringify(prev.categories)) {
          return { ...prev, categories: selectedCategories };
        }
        return prev;
      });
    }

    isCategoryDropdownOpenRef.current = isCategoryDropdownOpen;
  }, [isCategoryDropdownOpen, selectedCategories]);

  useEffect(() => {
    if (isProvinceDropdownOpenRef.current && !isProvinceDropdownOpen) {
      setLocalFilters(prev => {
        if (JSON.stringify(selectedProvinces) !== JSON.stringify(prev.provinces)) {
          return { ...prev, provinces: selectedProvinces };
        }
        return prev;
      });
    }

    isProvinceDropdownOpenRef.current = isProvinceDropdownOpen;
  }, [isProvinceDropdownOpen, selectedProvinces]);

  return (
    <div className="w-full">
      <Card className="h-auto">
        <CardContent className="space-y-6 mt-4 px-4 overflow-visible">
          <div className="space-y-2">
            <Label className="text-sm font-semibold">{t.search}</Label>
            <Input
              type="text"
              placeholder={t.searchPlaceholder}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold">{t.sorting}</Label>

            <Popover open={isOrderDropdownOpen} onOpenChange={setIsOrderDropdownOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={isOrderDropdownOpen}
                  className="w-full justify-between h-10"
                >
                  <span className="truncate">
                    {getSelectedOrderText()}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>

              <PopoverContent className="w-full p-0" align="start">
                <div className="p-3 space-y-3">
                  <div className="max-h-60 overflow-y-auto space-y-1">
                    {orderOptions.map((option) => (
                      <button
                        key={option.key}
                        onClick={() => handleSortChange(option.field, option.order)}
                        className={`flex items-center space-x-2 p-2 w-full text-left hover:bg-accent rounded-sm ${localFilters.sort?.field === option.field && localFilters.sort?.order === option.order ? 'bg-primary/10 text-primary' : ''}`}
                      >
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${localFilters.sort?.field === option.field && localFilters.sort?.order === option.order ? 'border-primary bg-primary' : 'border-gray-300'
                          }`}>
                          {localFilters.sort?.field === option.field && localFilters.sort?.order === option.order && <div className="w-2 h-2 rounded-full bg-white" />}
                        </div>
                        <span className="text-sm">
                          {option.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {enableSportNavigation && (
            <div className="space-y-3">
              <Label className="text-sm font-semibold">{t.sport || 'Deporte'}</Label>

              {loadingSports ? (
                <div className="flex justify-center items-center py-2">
                  <Spinner size='sm' />
                </div>
              ) : (
                <Popover open={isSportDropdownOpen} onOpenChange={setIsSportDropdownOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={isSportDropdownOpen}
                      className="w-full justify-between h-10"
                    >
                      <span className="truncate">
                        {getSelectedSportText()}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>

                  <PopoverContent className="w-full p-0" align="start">
                    <div className="p-3 space-y-3">
                      <Input
                        type="text"
                        placeholder={t.searchSportsPlaceholder || 'Buscar deporte...'}
                        value={sportSearchValue}
                        onChange={(e) => setSportSearchValue(e.target.value)}
                        className="h-8"
                      />

                      <div className="max-h-60 overflow-y-auto space-y-1">
                        <button
                          onClick={() => {
                            handleAllSportsSelect();
                            setIsSportDropdownOpen(false);
                          }}
                          className={`flex items-center space-x-2 p-2 w-full text-left hover:bg-accent rounded-sm ${!selectedSport ? 'bg-primary/10 text-primary' : ''}`}
                        >
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${!selectedSport ? 'border-primary bg-primary' : 'border-gray-300'}`}>
                            {!selectedSport && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                          <span className="text-sm font-medium">
                            {t.allSports || 'Todos los deportes'}
                          </span>
                        </button>

                        {filteredSports.map((sport) => (
                          <button
                            key={sport.id}
                            onClick={() => {
                              handleSportChange(sport.id);
                              setIsSportDropdownOpen(false);
                            }}
                            className={`flex items-center space-x-2 p-2 w-full text-left hover:bg-accent rounded-sm ${selectedSport === sport.id ? 'bg-primary/10 text-primary' : ''}`}
                          >
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selectedSport === sport.id ? 'border-primary bg-primary' : 'border-gray-300'}`}>
                              {selectedSport === sport.id && <div className="w-2 h-2 rounded-full bg-white" />}
                            </div>
                            <span className="text-sm">
                              {sport.name}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
          )}

          {(sportId && categories.length > 0) && (
            <div className="space-y-3">
              <Label className="text-sm font-semibold">{t.categories}</Label>

              <Popover open={isCategoryDropdownOpen} onOpenChange={setIsCategoryDropdownOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={isCategoryDropdownOpen}
                    className="w-full justify-between h-10"
                  >
                    <span className="truncate">
                      {getSelectedCategoryText()}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>

                <PopoverContent className="w-full p-0" align="start">
                  <div className="p-3 space-y-3">
                    <Input
                      type="text"
                      placeholder={t.searchCategoriesPlaceholder}
                      value={categorySearchValue}
                      onChange={(e) => setCategorySearchValue(e.target.value)}
                      className="h-8"
                    />

                    <div className="max-h-60 overflow-y-auto space-y-1">
                      {!enableSportNavigation && (
                        <button
                          onClick={handleAllCategoriesSelect}
                          className={`flex items-center space-x-2 p-2 w-full text-left hover:bg-accent rounded-sm ${selectedCategories.length === 0 ? 'bg-primary/10 text-primary' : ''}`}
                        >
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${selectedCategories.length === 0 ? 'border-primary bg-primary' : 'border-gray-300'}`}>
                            {selectedCategories.length === 0 && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <span className="text-sm font-medium">
                            {t.allCategories}
                          </span>
                        </button>
                      )}

                      {filteredCategories.map((category) => (
                        <button
                          key={category.key}
                          onClick={(e) => {
                            e.preventDefault();
                            handleCategoryChange(category.key)
                          }}
                          className={`flex items-center space-x-2 p-2 w-full text-left hover:bg-accent rounded-sm`}
                        >
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${selectedCategories.includes(category.key) ? 'border-primary bg-primary' : 'border-gray-300'}`}>
                            {selectedCategories.includes(category.key) && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <span className="text-sm">
                            {category.label as string}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          )}

          <div className="space-y-3">
            <Label className="text-sm font-semibold">{t.eventDate}</Label>

            <Popover open={isDateDropdownOpen} onOpenChange={setIsDateDropdownOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={isDateDropdownOpen}
                  className="w-full justify-between h-10"
                >
                  <span className="truncate">
                    {getSelectedDateFiltersText()}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0" align="start">
                <div className="p-3 space-y-3">
                  <div className="max-h-60 overflow-y-auto space-y-1">
                    <button
                      onClick={handleAllDateFiltersSelect}
                      className={`flex items-center space-x-2 p-2 w-full text-left hover:bg-accent rounded-sm ${selectedDateFilters.length === 0 || selectedDateFilters[0] === 'all' ? 'bg-primary/10 text-primary' : ''}`}
                    >
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selectedDateFilters.length === 0 || selectedDateFilters[0] === 'all' ? 'border-primary bg-primary' : 'border-gray-300'}`}>
                        {(selectedDateFilters.length === 0 || selectedDateFilters[0] === 'all') && <div className="w-2 h-2 rounded-full bg-white" />}
                      </div>
                      <span className="text-sm font-medium">
                        {t.allDateFilters}
                      </span>
                    </button>

                    {filteredDateOptions.map((option) => (
                      <button
                        key={option.key}
                        onClick={() => handleDateFilterSelect(option.key)}
                        className={`flex items-center space-x-2 p-2 w-full text-left hover:bg-accent rounded-sm ${selectedDateFilters.includes(option.key) ? 'bg-primary/10 text-primary' : ''}`}
                      >
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selectedDateFilters.includes(option.key) ? 'border-primary bg-primary' : 'border-gray-300'}`}>
                          {selectedDateFilters.includes(option.key) && <div className="w-2 h-2 rounded-full bg-white" />}
                        </div>
                        <span className="text-sm">
                          {option.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {showCustomDates && isDateFilterActive('custom') && (
              <div className="mt-3 space-y-3">
                <Label className="text-sm mb-2 block">{t.selectDateRange}</Label>

                <div className="space-y-2">
                  <Label className="text-xs">{t.fromDate}</Label>
                  <Popover open={isStartDatePopoverOpen} onOpenChange={setIsStartDatePopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full pl-3 text-left font-normal h-10",
                          !startDate && "text-muted-foreground"
                        )}
                      >
                        {startDate ? (
                          formatDateByLanguage(startDate)
                        ) : (
                          <span>{t.pickStartDate}</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        dict={dictionary}
                        isSingle={true}
                        props={{
                          mode: "single",
                          selected: startDate,
                          onSelect: handleStartDateChange,
                          fromYear: 1990,
                          toYear: 3000,
                        } as DayPickerSingleProps}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">{t.toDate}</Label>
                  <Popover open={isEndDatePopoverOpen} onOpenChange={setIsEndDatePopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full pl-3 text-left font-normal h-10",
                          !endDate && "text-muted-foreground"
                        )}
                      >
                        {endDate ? (
                          formatDateByLanguage(endDate)
                        ) : (
                          <span>{t.pickEndDate}</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        dict={dictionary}
                        isSingle={true}
                        props={{
                          mode: "single",
                          selected: endDate,
                          onSelect: handleEndDateChange,
                          disabled: (date) => startDate && date < startDate,
                          fromYear: 1990,
                          toYear: 3000,
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            )}
          </div>

          {!hideCountryFilter && (
            <div className="space-y-3">
              <Label className="text-sm font-semibold">{t.countries}</Label>

              <Button
                variant="outline"
                onClick={openModal}
                className="w-full justify-between h-10"
              >
                <span className="truncate">
                  {getSelectedCountriesText()}
                </span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </div>
          )}

          {!hideProvinceFilter && currentCountryData?.id && (
            <div className="space-y-3">
              <Label className="text-sm font-semibold">{t.provinces}</Label>

              {
                !(currentCountryData?.id && !(countries.length === 0) && !(loadingProvinces && provinces.length === 0)) ? (
                  <div className="space-y-3 flex justify-center items-center">
                    <Spinner size='md' />
                  </div>
                ) : (
                  <Popover open={isProvinceDropdownOpen} onOpenChange={setIsProvinceDropdownOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={isProvinceDropdownOpen}
                        className="w-full justify-between h-10"
                      >
                        <span className="truncate">
                          {getSelectedProvincesText()}
                        </span>
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <div className="p-3 space-y-3">
                        <Input
                          type="text"
                          placeholder={t.searchProvincesPlaceholder}
                          value={provinceSearchValue}
                          onChange={(e) => setProvinceSearchValue(e.target.value)}
                          className="h-8"
                        />

                        <div className="max-h-60 overflow-y-auto space-y-1">
                          <button
                            onClick={handleAllProvincesSelect}
                            className={`flex items-center space-x-2 p-2 w-full text-left hover:bg-accent rounded-sm ${selectedProvinces.length === 0 ? 'bg-primary/10 text-primary' : ''
                              }`}
                          >
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${selectedProvinces.length === 0 ? 'border-primary bg-primary' : 'border-gray-300'
                              }`}>
                              {selectedProvinces.length === 0 && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <span className="text-sm font-medium">
                              {t.allProvinces}
                            </span>
                          </button>

                          {filteredAndOrderedProvinces.slice(0, displayedProvincesCount).map((province) => (
                            <button
                              key={province.id}
                              onClick={(e) => {
                                e.preventDefault();
                                handleProvinceChange(province.id)
                              }}
                              className={`flex items-center space-x-2 p-2 w-full text-left hover:bg-accent rounded-sm`}
                            >
                              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${selectedProvinces.includes(province.id) ? 'border-primary bg-primary' : 'border-gray-300'
                                }`}>
                                {selectedProvinces.includes(province.id) && <Check className="w-3 h-3 text-white" />}
                              </div>
                              <span className="text-sm">
                                {province.value}
                              </span>
                            </button>
                          ))}

                          <div className="flex justify-between items-center">
                            {displayedProvincesCount > 10 ? (
                              <button
                                onClick={handleShowLessProvinces}
                                className="text-sm text-blue-600 hover:text-blue-800 underline cursor-pointer pt-2"
                              >
                                {t.showLess}
                              </button>
                            ) : <div className="h-4 pt-2" />}

                            {filteredAndOrderedProvinces.length > displayedProvincesCount && (
                              <button
                                onClick={handleShowMoreProvinces}
                                className="text-sm text-blue-600 hover:text-blue-800 underline cursor-pointer pt-2"
                              >
                                {t.showMore}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                )
              }
            </div>
          )}

          <div className="pt-4 space-y-3">
            <Button
              onClick={applyFilters}
              className="w-full"
            >
              {t.applyFilters}
            </Button>

            <Button
              variant="outline"
              onClick={clearFilters}
              className="w-full"
            >
              {t.clearFilters}
            </Button>

            {!hidePreferencesLink && isAuthenticated && (
              <p className="text-sm text-muted-foreground text-center">
                {lang === 'es' && (
                  <>También puedes filtrar por tus <Link href={`/${lang}/preferred`} className="text-primary hover:underline font-medium">Preferencias</Link></>
                )}
                {lang === 'en' && (
                  <>You can also filter by your <Link href={`/${lang}/preferred`} className="text-primary hover:underline font-medium">Preferences</Link></>
                )}
                {lang === 'pt' && (
                  <>Você também pode filtrar por suas <Link href={`/${lang}/preferred`} className="text-primary hover:underline font-medium">Preferências</Link></>
                )}
                {lang === 'ca' && (
                  <>També pots filtrar per les teves <Link href={`/${lang}/preferred`} className="text-primary hover:underline font-medium">Preferències</Link></>
                )}
                {lang === 'eu' && (
                  <>Zure <Link href={`/${lang}/preferred`} className="text-primary hover:underline font-medium">Hobespenak</Link> arabera ere filtra dezakezu</>
                )}
                {lang === 'fr' && (
                  <>Vous pouvez également filtrer par vos <Link href={`/${lang}/preferred`} className="text-primary hover:underline font-medium">Préférences</Link></>
                )}
                {lang === 'it' && (
                  <>Puoi anche filtrare per le tue <Link href={`/${lang}/preferred`} className="text-primary hover:underline font-medium">Preferenze</Link></>
                )}
                {!(config.i18n.locales as readonly string[]).includes(lang) && (
                  <>You can also filter by your <Link href={`/${lang}/preferred`} className="text-primary hover:underline font-medium">Preferences</Link></>
                )}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EventFilters;